import React, { useCallback, useContext, useMemo } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { toast } from "react-toastify";

import IconMinus from "../../../public/svg/icon-minus.svg";
import IconPlus from "../../../public/svg/icon-plus.svg";
import { CartContext } from "../../contexts/CartContext";
import { ExtendedCartMerchandise } from "../../types/shopify-components";
import { heading3 } from "../../utils/styles";

export const TEST_ID_TOAST = "toast";
export const TEST_ID_QUANTITY_CONTROL_DECREASE = "quantity-control-decrease";
export const TEST_ID_QUANTITY_CONTROL_INCREASE = "quantity-control-increase";

export interface QuantityControlProps {
  allowZero: boolean;
  connectedMerchandises?: ExtendedCartMerchandise[];
  quantity: number;
  merchandise: ExtendedCartMerchandise;
  updateOnChange: boolean;
}

function showNotAvailableNotification(
  message: "temporarily-not-available" | "limited-availability",
  ticketCount?: number,
): void {
  let element: React.ReactNode;
  if (message === "limited-availability" && ticketCount !== undefined) {
    element = (
      <FormattedMessage
        id="lineItem.limitedAvailability"
        values={{ ticketCount: Math.max(ticketCount, 0) }}
      />
    );
  } else {
    element = <FormattedMessage id="lineItem.temporarilyNotAvailable" />;
  }

  toast.info(
    <p className="ma0" data-testid={TEST_ID_TOAST}>
      {element}
    </p>,
    {
      toastId: "toast-product-is-temporarily-not-available",
    },
  );
}

const QuantityControl: React.FC<QuantityControlProps> = ({
  allowZero,
  connectedMerchandises,
  quantity,
  merchandise,
  updateOnChange,
}) => {
  const { formatMessage } = useIntl();
  const { increaseQuantity, decreaseQuantity, isAddingLines, isRemovingLines } =
    useContext(CartContext);

  const allMerchandises = useMemo(() => {
    return connectedMerchandises
      ? [merchandise, ...connectedMerchandises]
      : [merchandise];
  }, [connectedMerchandises, merchandise]);

  const handleDecrease = useCallback((): void => {
    if (merchandise.availableForSale) {
      decreaseQuantity({
        merchandises: allMerchandises,
        allowZero,
        shouldUpdate: updateOnChange,
      });
    } else {
      showNotAvailableNotification("temporarily-not-available");
    }
  }, [
    allowZero,
    allMerchandises,
    decreaseQuantity,
    updateOnChange,
    merchandise,
  ]);

  const handleIncrease = useCallback((): void => {
    if (!merchandise.availableForSale) {
      showNotAvailableNotification("temporarily-not-available");
    } else {
      increaseQuantity({
        merchandises: allMerchandises,
        shouldUpdate: updateOnChange,
      });
    }
  }, [allMerchandises, increaseQuantity, updateOnChange, merchandise]);

  return (
    <div className="flex items-center justify-end justify-center-l">
      <button
        className="ma0 pa2 bw0 bg-transparent pointer f0 hover-pink svg-icon"
        disabled={isAddingLines || isRemovingLines}
        onClick={handleDecrease}
        data-testid={TEST_ID_QUANTITY_CONTROL_DECREASE}
        title={formatMessage({ id: "quantityControls.decreaseQuantity" })}
        aria-label={formatMessage({ id: "quantityControls.decreaseQuantity" })}
      >
        <IconMinus />
      </button>
      <span
        className={`${heading3} w2 tc`}
        data-testid="quantity-control-quantity"
      >
        {quantity}
        <span className="clip">
          <FormattedMessage id="quantityControls.pcs" />
        </span>
      </span>
      <button
        className="ma0 pa2 bw0 bg-transparent pointer f0 hover-pink svg-icon"
        disabled={isAddingLines || isRemovingLines}
        onClick={handleIncrease}
        data-testid={TEST_ID_QUANTITY_CONTROL_INCREASE}
        title={formatMessage({ id: "quantityControls.increaseQuantity" })}
        aria-label={formatMessage({ id: "quantityControls.increaseQuantity" })}
      >
        <IconPlus />
      </button>
    </div>
  );
};

export default QuantityControl;
